import { neon } from '@/services/neon'

export type OtaProvider = 'airbnb' | 'booking' | 'vrbo' | 'google' | 'other'

export interface OtaSyncConfig {
  id: string
  property_id: string
  provider: OtaProvider
  import_url?: string | null
  is_active: boolean
  last_synced_at?: string | null
}

export interface OtaBooking {
  id: string
  property_id: string
  provider: string
  guest_name: string
  check_in: string
  check_out: string
}

export async function fetchOtaConfigs(propertyId: string): Promise<OtaSyncConfig[]> {
  const { data, error } = await neon
    .from('ota_sync_configs')
    .select('*')
    .eq('property_id', propertyId)
    .order('created_at', { ascending: true })

  if (error) throw new Error(error.message)
  return (data as OtaSyncConfig[]) || []
}

export async function createOtaConfig(
  propertyId: string,
  provider: string,
  importUrl: string,
): Promise<OtaSyncConfig> {
  const { data, error } = await neon
    .from('ota_sync_configs')
    .insert({ property_id: propertyId, provider, import_url: importUrl })
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as OtaSyncConfig
}

export async function updateOtaConfig(id: string, importUrl: string): Promise<OtaSyncConfig> {
  const { data, error } = await neon
    .from('ota_sync_configs')
    .update({ import_url: importUrl })
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as OtaSyncConfig
}

export async function deleteOtaConfig(id: string): Promise<void> {
  const { error } = await neon.from('ota_sync_configs').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

export async function fetchOtaBookings(propertyId: string): Promise<OtaBooking[]> {
  const { data, error } = await neon
    .from('ota_bookings')
    .select('*')
    .eq('property_id', propertyId)
    .order('check_in', { ascending: true })

  if (error) throw new Error(error.message)
  return (data as OtaBooking[]) || []
}

export function parseICal(
  ics: string,
): Array<{ uid: string; checkIn: string; checkOut: string; summary: string }> {
  const events: Array<{ uid: string; checkIn: string; checkOut: string; summary: string }> = []
  const blocks = ics.split('BEGIN:VEVENT')
  for (const block of blocks.slice(1)) {
    const endIdx = block.indexOf('END:VEVENT')
    const vevent = block.slice(0, endIdx)
    const uid = vevent.match(/UID:(.+)/)?.[1]?.trim()
    // Google uses DTSTART;VALUE=DATE:20250101 or DTSTART:20250101T000000Z — capture 8-digit date
    const dtstart = vevent.match(/DTSTART[^:]*:(\d{8})/)?.[1]
    const dtend = vevent.match(/DTEND[^:]*:(\d{8})/)?.[1]
    const summary = vevent.match(/SUMMARY:(.+)/)?.[1]?.trim() || 'OTA Guest'
    if (uid && dtstart && dtend) {
      events.push({
        uid,
        checkIn: `${dtstart.slice(0, 4)}-${dtstart.slice(4, 6)}-${dtstart.slice(6, 8)}`,
        checkOut: `${dtend.slice(0, 4)}-${dtend.slice(4, 6)}-${dtend.slice(6, 8)}`,
        summary,
      })
    }
  }
  return events
}

async function fetchICal(url: string): Promise<string> {
  // Use server proxy in browser to avoid CORS (Airbnb, Google, Booking block cross-origin)
  const isBrowser = typeof window !== 'undefined'
  const fetchUrl = isBrowser ? `/api/ical-proxy?url=${encodeURIComponent(url)}` : url
  const res = await fetch(fetchUrl)
  if (!res.ok) throw new Error(`iCal fetch failed: ${res.status}`)
  return res.text()
}

export async function syncOtaCalendar(config: OtaSyncConfig): Promise<{ imported: number }> {
  if (!config.import_url) throw new Error('No import URL configured')

  const ics = await fetchICal(config.import_url)
  const events = parseICal(ics)

  const existing = await neon.from('ota_bookings').select('provider_uid').eq('config_id', config.id)
  const existingUids = new Set(
    ((existing.data as Array<{ provider_uid: string }>) || []).map((e) => e.provider_uid),
  )
  const newUids = new Set(events.map((e) => e.uid))

  for (const event of events) {
    await neon.from('ota_bookings').upsert(
      {
        property_id: config.property_id,
        config_id: config.id,
        provider: config.provider,
        provider_uid: event.uid,
        guest_name: event.summary,
        check_in: event.checkIn,
        check_out: event.checkOut,
      },
      { onConflict: 'config_id,provider_uid' },
    )
  }

  const stale = [...existingUids].filter((uid) => !newUids.has(uid))
  for (const uid of stale) {
    await neon.from('ota_bookings').delete().eq('config_id', config.id).eq('provider_uid', uid)
  }

  await neon
    .from('ota_sync_configs')
    .update({ last_synced_at: new Date().toISOString() })
    .eq('id', config.id)

  return { imported: events.length }
}
